import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import TONDashboard from "./pages/TONDashboard";
import WalletManager from "./pages/WalletManager";
import MarketMaker from "./pages/MarketMaker";
import TokenTracker from "./pages/TokenTracker";
import WalletWarmup from "./pages/WalletWarmup";
import UserSettings from "./pages/UserSettings";
import Layout from "./components/Layout";
import Login from "./components/Login";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { TonConnectUIProvider } from "@tonconnect/ui-react";
import { Wallet, BarChart3, Bot, Target, Flame } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const navigate = useNavigate();

  const helloWorldApi = async () => {
    try {
      const response = await axios.get(`${API}/`);
      console.log(response.data.message);
    } catch (e) {
      console.error(e, `errored out requesting / api`);
    }
  };

  useEffect(() => {
    helloWorldApi();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-6">
      <div className="max-w-6xl w-full">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            TON DEX Platform
          </h1>
          <p className="text-xl text-gray-600">
            Advanced tools for TON blockchain trading and wallet management
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div 
            onClick={() => navigate('/token-tracker')}
            className="bg-white rounded-lg shadow-lg p-8 cursor-pointer hover:shadow-xl transition-shadow"
          >
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="p-4 bg-blue-100 rounded-full">
                <Target className="h-12 w-12 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold">Token Analytics</h2>
              <p className="text-gray-600">
                Holder analysis, P/L tracking, and detailed trade history
              </p>
              <button className="mt-4 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                View Analytics
              </button>
            </div>
          </div>

          <div 
            onClick={() => navigate('/ton')}
            className="bg-white rounded-lg shadow-lg p-8 cursor-pointer hover:shadow-xl transition-shadow"
          >
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="p-4 bg-indigo-100 rounded-full">
                <BarChart3 className="h-12 w-12 text-indigo-600" />
              </div>
              <h2 className="text-2xl font-bold">Token Tracker</h2>
              <p className="text-gray-600">
                Monitor tokens, view price charts, and track trades in real-time
              </p>
              <button className="mt-4 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors">
                Open Tracker
              </button>
            </div>
          </div>

          <div 
            onClick={() => navigate('/wallets')}
            className="bg-white rounded-lg shadow-lg p-8 cursor-pointer hover:shadow-xl transition-shadow"
          >
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="p-4 bg-purple-100 rounded-full">
                <Wallet className="h-12 w-12 text-purple-600" />
              </div>
              <h2 className="text-2xl font-bold">Wallet Manager</h2>
              <p className="text-gray-600">
                Generate and manage multiple wallets for mass trading operations
              </p>
              <button className="mt-4 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
                Manage Wallets
              </button>
            </div>
          </div>

          <div 
            onClick={() => navigate('/market-maker')}
            className="bg-white rounded-lg shadow-lg p-8 cursor-pointer hover:shadow-xl transition-shadow"
          >
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="p-4 bg-green-100 rounded-full">
                <Bot className="h-12 w-12 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold">Market Maker</h2>
              <p className="text-gray-600">
                Automated trading strategy against enemy wallets
              </p>
              <button className="mt-4 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                Configure Strategy
              </button>
            </div>
          </div>

          <div 
            onClick={() => navigate('/wallet-warmup')}
            className="bg-white rounded-lg shadow-lg p-8 cursor-pointer hover:shadow-xl transition-shadow"
          >
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="p-4 bg-orange-100 rounded-full">
                <Flame className="h-12 w-12 text-orange-600" />
              </div>
              <h2 className="text-2xl font-bold">Wallet Warmup</h2>
              <p className="text-gray-600">
                Automatic wallet warming through token swaps
              </p>
              <button className="mt-4 px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors">
                Open Warmup
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <TonConnectUIProvider manifestUrl="https://tradeton.preview.emergentagent.com/tonconnect-manifest.json">
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login onLoginSuccess={() => {}} />} />
            <Route path="/" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
            <Route path="/ton" element={<ProtectedRoute><Layout><TONDashboard /></Layout></ProtectedRoute>} />
            <Route path="/wallets" element={<ProtectedRoute><Layout><WalletManager /></Layout></ProtectedRoute>} />
            <Route path="/market-maker" element={<ProtectedRoute><Layout><MarketMaker /></Layout></ProtectedRoute>} />
            <Route path="/token-tracker" element={<ProtectedRoute><Layout><TokenTracker /></Layout></ProtectedRoute>} />
            <Route path="/wallet-warmup" element={<ProtectedRoute><Layout><WalletWarmup /></Layout></ProtectedRoute>} />
            <Route path="/user-settings" element={<ProtectedRoute requireAdmin={true}><Layout><UserSettings /></Layout></ProtectedRoute>} />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </TonConnectUIProvider>
    </AuthProvider>
  );
}

export default App;
